/**
 * FIELDS-1 — the "captured figures" block.
 *
 * Everything here is persisted inside `NormalizationSession.form_extras_json`
 * and round-tripped by GET/PUT `/qoe/deals/{deal_id}/normalization`. The server
 * side of the same shape is documented in
 * `apps/api/routers/qoe/deals/_capture.py` — keep the two in step.
 *
 * Sub-dicts are keyed by fiscal year as a STRING, because that is what a JSON
 * object key is. Read them with `extras.bank_periods?.[String(year)]`.
 */

export type ReportingBasis = 'cash' | 'accrual'

/** One fiscal period of proof-of-cash figures the analyst keyed in. */
export interface BankCapturePeriod {
  bank_deposits?: number | null
}

/** One fiscal period of balance-sheet figures — the only input the NWC peg has.
 *
 *  Every field is optional AND nullable, and the two mean different things:
 *  absent/null is "not entered yet" (the peg reports pending), while `inventory`
 *  set to 0 is a real answer for a service business. */
export interface BalanceSheetCapturePeriod {
  accounts_receivable?: number | null
  inventory?: number | null
  accounts_payable?: number | null
  accrued_liabilities?: number | null
  cash?: number | null
}

export const BALANCE_SHEET_FIELDS = [
  'accounts_receivable',
  'inventory',
  'accounts_payable',
  'accrued_liabilities',
  'cash',
] as const

export type BalanceSheetField = typeof BALANCE_SHEET_FIELDS[number]

/** One line of the funded-debt schedule.
 *
 *  `description` is required because the equity bridge lists these items
 *  individually to the seller — "Total debt $840,000" is not confirmable. */
export interface DebtScheduleItem {
  description: string
  amount: number
  note?: string | null
}

/** FIELDS-1 P1b: enterprise value and debt-like items intake.
 *
 *  `enterprise_value` here is a real valuation input, distinct from the
 *  engagement sizing proxy on the executive summary. When both exist, this one
 *  is what the bridge is built on. */
export interface EquityBridgeCapture {
  enterprise_value?: number | null
  enterprise_value_low?: number | null
  enterprise_value_high?: number | null
  /** Total funded debt. Takes precedence over the sum of `debt_schedule`, which
   *  in turn takes precedence over the balance sheet's debt lines. */
  funded_debt?: number | null
  deferred_revenue?: number | null
  customer_deposits?: number | null
  /** Omitting the key preserves the stored schedule; sending `[]` clears it. */
  debt_schedule?: DebtScheduleItem[] | null
}

export interface CaptureExtras {
  /** Deal-level accounting basis. Absent means "not stated" — the corroboration
   *  handler then assumes accrual and says so in its caveat. */
  reporting_basis?: ReportingBasis | null
  /** How many bank accounts the seller says exist. Absent leaves account
   *  completeness UNVERIFIED — the card cannot claim a complete reconciliation
   *  without knowing what "complete" would be. */
  declared_bank_accounts?: number | null
  bank_periods?: Record<string, BankCapturePeriod> | null
  /** FIELDS-1 P1a: per-fiscal-year balance sheet, keyed by year-as-string. */
  balance_sheet?: Record<string, BalanceSheetCapturePeriod> | null
  /** FIELDS-1 P1b: deal-level, not per-period — an enterprise value is a single
   *  figure as of the valuation date. */
  equity_bridge?: EquityBridgeCapture | null
}

/** The normalization GET returns this block; absent on sessions saved before
 *  FIELDS-1, hence the `?`. */
export interface NormalizationCaptureEnvelope {
  form_extras_json?: CaptureExtras | null
}
